import { createSeededRandom } from "../random/seeded-random";
import { isZeroResourcePool, normalizeResourcePool } from "../resources/resource-pool";
import {
  getArmyDefinitionForInstance,
  getCardDefinition,
  getCardInstanceDefinition,
  getCommanderDefinitionForInstance,
  getLandDefinitionForInstance,
} from "../selectors/card-instances";
import { MAX_ARMY_CARDS_PER_LANE } from "../selectors/garrison";
import { getSupplySnapshot } from "../selectors/supply";
import { getActiveLandLaneIds } from "../selectors/formation";
import {
  LANE_IDS,
  PLAYER_IDS,
  type CardInstanceId,
  type GameState,
  MAX_HAND_SIZE,
  type LaneId,
  type PlayerId,
  type PlayerLaneState,
  type ResourcePool,
  type TurnActionUsage,
} from "../types";
import { getOpponentPlayerId } from "../types";

export interface StateInvariantViolation {
  readonly code: string;
  readonly path: string;
  readonly message: string;
}

function violation(code: string, path: string, message: string): StateInvariantViolation {
  return { code, path, message };
}

function isFiniteNonNegative(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

function inspectCardInstanceUniqueness(
  state: Readonly<GameState>,
): readonly StateInvariantViolation[] {
  const violations: StateInvariantViolation[] = [];
  const firstPathById = new Map<CardInstanceId, string>();

  const record = (cardInstanceId: CardInstanceId, path: string, locationOwner: PlayerId) => {
    if (
      state.schemaVersion === 2 ||
      state.schemaVersion === 3 ||
      state.schemaVersion === 4 ||
      state.schemaVersion === 5
    ) {
      const instance = state.cardInstances[cardInstanceId];
      if (instance === undefined) {
        violations.push(
          violation(
            "CARD_INSTANCE_NOT_REGISTERED",
            path,
            `Card instance ${cardInstanceId} has no schema-V2 manifest entry.`,
          ),
        );
      } else if (instance.ownerPlayerId !== locationOwner) {
        violations.push(
          violation(
            "CARD_INSTANCE_OWNER_MISMATCH",
            path,
            `Card instance ${cardInstanceId} is located under ${locationOwner} but belongs to ${instance.ownerPlayerId}.`,
          ),
        );
      }
    }

    const firstPath = firstPathById.get(cardInstanceId);
    if (firstPath === undefined) {
      firstPathById.set(cardInstanceId, path);
      return;
    }

    violations.push(
      violation(
        "CARD_INSTANCE_NOT_UNIQUE",
        path,
        `Card instance ${cardInstanceId} already occupies ${firstPath}.`,
      ),
    );
  };

  for (const playerId of PLAYER_IDS) {
    const player = state.players[playerId];
    const zoneEntries = [
      ["deck", player.deck],
      ["hand", player.hand],
      ["discard", player.discard],
      ["exhaust", player.exhaust],
      ["eventResolution", player.eventResolution],
    ] as const;

    for (const [zone, cardInstanceIds] of zoneEntries) {
      cardInstanceIds.forEach((cardInstanceId, index) => {
        record(cardInstanceId, `players.${playerId}.${zone}.${index}`, playerId);
      });
    }

    if (player.activePolicyId !== null) {
      record(player.activePolicyId, `players.${playerId}.activePolicyId`, playerId);
    }
  }

  for (const laneId of LANE_IDS) {
    for (const playerId of PLAYER_IDS) {
      const side = state.lanes[laneId].players[playerId];
      const path = `lanes.${laneId}.players.${playerId}`;

      side.land?.cardInstanceIds.forEach((cardInstanceId, index) => {
        record(cardInstanceId, `${path}.land.cardInstanceIds.${index}`, playerId);
      });
      if (side.commander !== null) {
        record(side.commander.cardInstanceId, `${path}.commander.cardInstanceId`, playerId);
      }
      side.armies.forEach((army, index) => {
        record(army.cardInstanceId, `${path}.armies.${index}.cardInstanceId`, playerId);
      });
      if (side.conspiracyCardInstanceId !== null) {
        record(side.conspiracyCardInstanceId, `${path}.conspiracyCardInstanceId`, playerId);
      }
      side.attachedCardInstanceIds.forEach((cardInstanceId, index) => {
        record(cardInstanceId, `${path}.attachedCardInstanceIds.${index}`, playerId);
      });
    }
  }

  if (
    state.schemaVersion === 2 ||
    state.schemaVersion === 3 ||
    state.schemaVersion === 4 ||
    state.schemaVersion === 5
  ) {
    const instanceIds = Object.keys(state.cardInstances).sort((left, right) =>
      left < right ? -1 : left > right ? 1 : 0,
    );
    for (const cardInstanceId of instanceIds) {
      const instance = state.cardInstances[cardInstanceId];
      if (instance === undefined) continue;

      if (instance.cardInstanceId !== cardInstanceId) {
        violations.push(
          violation(
            "CARD_INSTANCE_ID_MISMATCH",
            `cardInstances.${cardInstanceId}.cardInstanceId`,
            "The manifest key and cardInstanceId must match.",
          ),
        );
      }
      if (getCardDefinition(state, instance.cardDefinitionId) === undefined) {
        violations.push(
          violation(
            "CARD_INSTANCE_DEFINITION_NOT_FOUND",
            `cardInstances.${cardInstanceId}.cardDefinitionId`,
            `Card instance ${cardInstanceId} references an unknown definition.`,
          ),
        );
      }
      if (!firstPathById.has(cardInstanceId)) {
        violations.push(
          violation(
            "CARD_INSTANCE_HAS_NO_LOCATION",
            `cardInstances.${cardInstanceId}`,
            `Card instance ${cardInstanceId} occupies no legal zone or battlefield location.`,
          ),
        );
      }
    }
  }

  return violations;
}

function inspectLaneSide(
  state: Readonly<GameState>,
  laneId: LaneId,
  playerId: PlayerId,
  side: PlayerLaneState,
): readonly StateInvariantViolation[] {
  const path = `lanes.${laneId}.players.${playerId}`;
  const violations: StateInvariantViolation[] = [];
  const stationedEntryCount =
    (side.land?.cardInstanceIds.length ?? 0) +
    (side.commander === null ? 0 : 1) +
    side.armies.length +
    (side.conspiracyCardInstanceId === null ? 0 : 1) +
    side.attachedCardInstanceIds.length +
    side.combatModifiers.length;

  if (side.status === "empty" && stationedEntryCount > 0) {
    violations.push(
      violation(
        "EMPTY_LANE_HAS_CARDS",
        path,
        "An empty lane side cannot contain battlefield cards.",
      ),
    );
  }

  if ((side.status === "active" || side.status === "committed") && side.land === null) {
    violations.push(
      violation(
        "OCCUPIED_LANE_MISSING_LAND",
        `${path}.land`,
        "An active or committed lane side must contain a Land.",
      ),
    );
  }

  if (side.status === "defeated" && stationedEntryCount > 0) {
    violations.push(
      violation(
        "DEFEATED_LANE_HAS_CARDS",
        path,
        "Cards from a defeated lane must be in the Exhaust Zone.",
      ),
    );
  }

  if (side.land !== null) {
    if (
      !Number.isFinite(side.land.currentControl) ||
      !Number.isFinite(side.land.maximumControl) ||
      side.land.currentControl < 0 ||
      side.land.currentControl > side.land.maximumControl ||
      side.land.maximumControl <= 0
    ) {
      violations.push(
        violation(
          "INVALID_LAND_CONTROL",
          `${path}.land.currentControl`,
          "Land Control must be finite and between zero and maximum Control.",
        ),
      );
    }

    if (side.status === "active" && side.land.currentControl === 0) {
      violations.push(
        violation(
          "UNRESOLVED_DEFEATED_LAND",
          `${path}.land.currentControl`,
          "An active Land at zero Control must be resolved as defeated.",
        ),
      );
    }

    if (!Number.isFinite(side.land.attackBonus) || !Number.isFinite(side.land.guardBonus)) {
      violations.push(
        violation(
          "INVALID_LAND_COMBAT_BONUS",
          `${path}.land`,
          "Land combat bonuses must be finite numbers.",
        ),
      );
    }

    if (
      state.schemaVersion === 2 ||
      state.schemaVersion === 3 ||
      state.schemaVersion === 4 ||
      state.schemaVersion === 5
    ) {
      const stack = side.land.cardInstanceIds;
      if (stack.length < 1 || stack.length > 3 || stack.length !== side.land.stage) {
        violations.push(
          violation(
            "LAND_STAGE_STACK_MISMATCH",
            `${path}.land.cardInstanceIds`,
            "A Land stack must contain exactly one card per current Stage.",
          ),
        );
      }

      const definitions = stack.map((cardInstanceId) =>
        getLandDefinitionForInstance(state, cardInstanceId),
      );
      if (definitions.some((definition) => definition === undefined)) {
        violations.push(
          violation(
            "LAND_STACK_DEFINITION_NOT_FOUND",
            `${path}.land.cardInstanceIds`,
            "Every Land stack card must resolve to a Land definition.",
          ),
        );
      } else {
        const firstDefinition = definitions[0];
        if (
          firstDefinition === undefined ||
          definitions.some(
            (definition) => definition?.developmentId !== firstDefinition.developmentId,
          )
        ) {
          violations.push(
            violation(
              "LAND_DEVELOPMENT_ID_MISMATCH",
              `${path}.land.cardInstanceIds`,
              "Every card in a Land stack must share one development identity.",
            ),
          );
        } else {
          if (
            (state.schemaVersion === 3 || state.schemaVersion === 4 || state.schemaVersion === 5) &&
            (!("landTypeId" in firstDefinition) ||
              definitions.some(
                (definition) =>
                  definition === undefined ||
                  !("landTypeId" in definition) ||
                  definition.landTypeId !== firstDefinition.landTypeId,
              ))
          ) {
            violations.push(
              violation(
                "LAND_TYPE_ID_MISMATCH",
                `${path}.land.cardInstanceIds`,
                "Every card in a Land stack must share one Land Type.",
              ),
            );
          }
          const currentStage = firstDefinition.stages[side.land.stage - 1];
          if (currentStage?.maximumControl !== side.land.maximumControl) {
            violations.push(
              violation(
                "LAND_STAGE_CONTROL_MISMATCH",
                `${path}.land.maximumControl`,
                "Land maximum Control must match its validated current Stage.",
              ),
            );
          }
        }
      }
    }
  }

  if (side.armies.length > MAX_ARMY_CARDS_PER_LANE) {
    violations.push(
      violation(
        "TOO_MANY_ARMY_CARDS",
        `${path}.armies`,
        `A lane cannot contain more than ${String(MAX_ARMY_CARDS_PER_LANE)} Army cards.`,
      ),
    );
  }

  if (side.commander !== null) {
    const totalArmySize = side.armies.reduce((total, army) => total + army.armySize, 0);

    if (
      state.schemaVersion === 2 ||
      state.schemaVersion === 3 ||
      state.schemaVersion === 4 ||
      state.schemaVersion === 5
    ) {
      const commanderDefinition = getCardInstanceDefinition(state, side.commander.cardInstanceId);
      if (commanderDefinition === undefined) {
        violations.push(
          violation(
            "COMMANDER_CATALOG_DEFINITION_NOT_FOUND",
            `${path}.commander.cardInstanceId`,
            "Every Commander permanent must resolve to an embedded catalog definition.",
          ),
        );
      } else if (commanderDefinition.type !== "commander") {
        violations.push(
          violation(
            "COMMANDER_CARD_TYPE_MISMATCH",
            `${path}.commander.cardInstanceId`,
            "A Commander permanent must reference a Commander card definition.",
          ),
        );
      }
    }

    if (
      (state.schemaVersion === 3 || state.schemaVersion === 4) &&
      state.catalog.schemaVersion === 3
    ) {
      const commanderDefinition = getCommanderDefinitionForInstance(
        state,
        side.commander.cardInstanceId,
      );
      if (commanderDefinition === undefined) {
        violations.push(
          violation(
            "COMMANDER_SCHEMA_THREE_DEFINITION_NOT_FOUND",
            `${path}.commander.cardInstanceId`,
            "Every catalog-schema-3 Commander permanent must resolve to projected printed stats.",
          ),
        );
      } else if (
        side.commander.attack !== commanderDefinition.attack ||
        side.commander.guard !== commanderDefinition.guard ||
        side.commander.armyCapacity !== commanderDefinition.armyCapacity
      ) {
        violations.push(
          violation(
            "COMMANDER_PRINTED_STATS_MISMATCH",
            `${path}.commander`,
            "Commander permanent printed stats must match the embedded schema-3 catalog definition.",
          ),
        );
      }
    }

    if (
      state.schemaVersion !== 5 &&
      (side.commander.armyCapacity === undefined ||
        !Number.isFinite(side.commander.armyCapacity) ||
        side.commander.armyCapacity < 0 ||
        side.commander.armyCapacity > 5)
    ) {
      violations.push(
        violation(
          "INVALID_COMMANDER_CAPACITY",
          `${path}.commander.armyCapacity`,
          "Commander Army Capacity must be between zero and five.",
        ),
      );
    }

    if (!isFiniteNonNegative(side.commander.attack) || !isFiniteNonNegative(side.commander.guard)) {
      violations.push(
        violation(
          "INVALID_COMMANDER_COMBAT_STAT",
          `${path}.commander`,
          "Commander Attack and Guard must be finite non-negative numbers.",
        ),
      );
    }

    if (
      state.schemaVersion !== 5 &&
      side.commander.armyCapacity !== undefined &&
      totalArmySize > side.commander.armyCapacity
    ) {
      violations.push(
        violation(
          "ARMY_CAPACITY_EXCEEDED",
          `${path}.armies`,
          "Total Army Size exceeds the Commander's Army Capacity.",
        ),
      );
    }
  }

  for (let index = 0; index < side.armies.length; index += 1) {
    const army = side.armies[index];
    if (army === undefined) continue;

    if (
      !isFiniteNonNegative(army.armySize) ||
      !isFiniteNonNegative(army.attack) ||
      !isFiniteNonNegative(army.guard)
    ) {
      violations.push(
        violation(
          "INVALID_ARMY_STAT",
          `${path}.armies.${index}`,
          "Army Size, Attack, and Guard must be finite non-negative numbers.",
        ),
      );
    }
  }

  for (let index = 0; index < side.combatModifiers.length; index += 1) {
    const modifier = side.combatModifiers[index];
    if (modifier === undefined) continue;

    if (!Number.isFinite(modifier.attack) || !Number.isFinite(modifier.guard)) {
      violations.push(
        violation(
          "INVALID_COMBAT_MODIFIER",
          `${path}.combatModifiers.${index}`,
          "Resolved combat modifiers must contain finite values.",
        ),
      );
    }
  }

  return violations;
}

function inspectSetupState(state: Readonly<GameState>): readonly StateInvariantViolation[] {
  if (
    state.schemaVersion !== 2 &&
    state.schemaVersion !== 3 &&
    state.schemaVersion !== 4 &&
    state.schemaVersion !== 5
  )
    return [];

  const violations: StateInvariantViolation[] = [];
  const validCatalogSchema =
    state.schemaVersion === 2
      ? state.catalog.schemaVersion === 1
      : state.schemaVersion === 5
        ? state.catalog.schemaVersion === 4
        : state.catalog.schemaVersion === 2 || state.catalog.schemaVersion === 3;
  if (
    !validCatalogSchema ||
    state.catalog.cardDataVersion !== state.cardDataVersion ||
    state.catalog.gameplayCatalogHash !== state.setup.gameplayCatalogHash ||
    state.setup.gameplayCatalogHash.length === 0
  ) {
    violations.push(
      violation(
        "SETUP_CATALOG_MISMATCH",
        "setup.gameplayCatalogHash",
        "Setup metadata must match a compatible stored engine catalog and card-data version.",
      ),
    );
  }

  const expectedInitialRandom = createSeededRandom(state.seed);
  const initialRandom = state.setup.initialRandomState;
  const finalSetupRandom = state.setup.finalSetupRandomState;
  if (
    initialRandom.algorithm !== expectedInitialRandom.algorithm ||
    initialRandom.seed !== expectedInitialRandom.seed ||
    initialRandom.value !== expectedInitialRandom.value ||
    initialRandom.draws !== 0
  ) {
    violations.push(
      violation(
        "INVALID_SETUP_INITIAL_RANDOM_STATE",
        "setup.initialRandomState",
        "Setup must retain the exact seeded random state before its first draw.",
      ),
    );
  }
  if (
    finalSetupRandom.algorithm !== "xorshift32" ||
    finalSetupRandom.seed !== state.seed ||
    !Number.isSafeInteger(finalSetupRandom.value) ||
    finalSetupRandom.value < 0 ||
    finalSetupRandom.value > 0xffff_ffff ||
    !Number.isSafeInteger(finalSetupRandom.draws) ||
    finalSetupRandom.draws < initialRandom.draws ||
    state.random.draws < finalSetupRandom.draws
  ) {
    violations.push(
      violation(
        "INVALID_SETUP_FINAL_RANDOM_STATE",
        "setup.finalSetupRandomState",
        "Setup must retain a valid final RNG state that precedes or equals the live RNG state.",
      ),
    );
  }

  if (!PLAYER_IDS.includes(state.setup.startingPlayerId)) {
    violations.push(
      violation(
        "INVALID_STARTING_PLAYER",
        "setup.startingPlayerId",
        "Setup must record a valid starting player.",
      ),
    );
  }

  for (const playerId of PLAYER_IDS) {
    const status = state.firstTurnDrawStatus[playerId];
    if (status !== "pending" && status !== "consumed") {
      violations.push(
        violation(
          "INVALID_FIRST_TURN_DRAW_STATUS",
          `firstTurnDrawStatus.${playerId}`,
          "First-turn Draw status must be pending or consumed.",
        ),
      );
    }

    const manifestEntries = Object.keys(state.cardInstances)
      .sort((left, right) => (left < right ? -1 : left > right ? 1 : 0))
      .map((cardInstanceId) => state.cardInstances[cardInstanceId])
      .filter(
        (instance): instance is NonNullable<typeof instance> =>
          instance !== undefined && instance.ownerPlayerId === playerId,
      );
    if (manifestEntries.length !== 40) {
      violations.push(
        violation(
          "INVALID_INITIAL_CARD_COUNT",
          "cardInstances",
          `Schema-V2 matches must register exactly 40 cards for ${playerId}.`,
        ),
      );
    }

    const ordinals = new Set<number>();
    for (const instance of manifestEntries) {
      if (
        !Number.isSafeInteger(instance.originalDeckOrdinal) ||
        instance.originalDeckOrdinal < 0 ||
        instance.originalDeckOrdinal >= 40 ||
        ordinals.has(instance.originalDeckOrdinal)
      ) {
        violations.push(
          violation(
            "INVALID_ORIGINAL_DECK_ORDINAL",
            `cardInstances.${instance.cardInstanceId}.originalDeckOrdinal`,
            "Original deck ordinals must be unique integers from zero through 39 per player.",
          ),
        );
      }
      ordinals.add(instance.originalDeckOrdinal);
    }

    const opening = state.setup.openingHands[playerId];
    const uniqueOpening = new Set(opening.acceptedCardInstanceIds);
    const hasOpeningLand = opening.acceptedCardInstanceIds.some(
      (cardInstanceId) => getLandDefinitionForInstance(state, cardInstanceId) !== undefined,
    );
    const openingOwnedByPlayer = opening.acceptedCardInstanceIds.every(
      (cardInstanceId) => state.cardInstances[cardInstanceId]?.ownerPlayerId === playerId,
    );
    if (
      opening.acceptedCardInstanceIds.length !== 5 ||
      uniqueOpening.size !== 5 ||
      !hasOpeningLand ||
      !openingOwnedByPlayer ||
      !Number.isSafeInteger(opening.drawAttempts) ||
      opening.drawAttempts < 1 ||
      opening.rejectedHands.length !== opening.drawAttempts - 1
    ) {
      violations.push(
        violation(
          "INVALID_OPENING_HAND_AUDIT",
          `setup.openingHands.${playerId}`,
          "Opening-hand audit must record five unique cards including a Land and every rejected attempt.",
        ),
      );
    }

    for (let index = 0; index < opening.rejectedHands.length; index += 1) {
      const rejected = opening.rejectedHands[index];
      if (
        rejected === undefined ||
        rejected.length !== 5 ||
        new Set(rejected).size !== 5 ||
        rejected.some(
          (cardInstanceId) => state.cardInstances[cardInstanceId]?.ownerPlayerId !== playerId,
        ) ||
        rejected.some((cardInstanceId) =>
          Boolean(getLandDefinitionForInstance(state, cardInstanceId)),
        )
      ) {
        violations.push(
          violation(
            "INVALID_REJECTED_OPENING_HAND",
            `setup.openingHands.${playerId}.rejectedHands.${index}`,
            "Every rejected opening hand must contain exactly five cards and no Land.",
          ),
        );
      }
    }

    const landActions = state.players[playerId].actionsUsed.landActions;
    if (!Number.isSafeInteger(landActions) || landActions < 0 || landActions > 1) {
      violations.push(
        violation(
          "INVALID_LAND_ACTION_USAGE",
          `players.${playerId}.actionsUsed.landActions`,
          "Land Action usage must be zero or one.",
        ),
      );
    }

    const deploymentActions = state.players[playerId].actionsUsed.deploymentActions;
    if (
      !Number.isSafeInteger(deploymentActions) ||
      deploymentActions < 0 ||
      deploymentActions > 1
    ) {
      violations.push(
        violation(
          "INVALID_DEPLOYMENT_ACTION_USAGE",
          `players.${playerId}.actionsUsed.deploymentActions`,
          "Deployment Action usage must be zero or one.",
        ),
      );
    }
  }

  return violations;
}

function resourcesEqual(left: ResourcePool, right: ResourcePool): boolean {
  return (
    left.food === right.food &&
    left.production === right.production &&
    left.trade === right.trade &&
    left.science === right.science &&
    left.culture === right.culture
  );
}

function inspectSupplyState(state: Readonly<GameState>): readonly StateInvariantViolation[] {
  const violations: StateInvariantViolation[] = [];

  for (const playerId of PLAYER_IDS) {
    try {
      normalizeResourcePool(state.players[playerId].kingdomPool);
    } catch {
      violations.push(
        violation(
          "INVALID_KINGDOM_POOL",
          `players.${playerId}.kingdomPool`,
          "Kingdom Pool values must be non-negative safe integers.",
        ),
      );
    }
  }

  if (state.schemaVersion !== 3 && state.schemaVersion !== 4 && state.schemaVersion !== 5) {
    return violations;
  }

  for (const laneId of LANE_IDS) {
    for (const playerId of PLAYER_IDS) {
      const side = state.lanes[laneId].players[playerId];
      for (let index = 0; index < side.armies.length; index += 1) {
        const army = side.armies[index];
        if (army === undefined) continue;
        const definition = getArmyDefinitionForInstance(state, army.cardInstanceId);
        if (definition === undefined) {
          violations.push(
            violation(
              "ARMY_CATALOG_DEFINITION_NOT_FOUND",
              `lanes.${laneId}.players.${playerId}.armies.${index}.cardInstanceId`,
              "Every schema-V3 Army permanent must resolve to a schema-2 Army definition.",
            ),
          );
        } else if (
          army.armySize !== definition.armySize ||
          army.attack !== definition.attack ||
          army.guard !== definition.guard
        ) {
          violations.push(
            violation(
              "ARMY_PRINTED_STATS_MISMATCH",
              `lanes.${laneId}.players.${playerId}.armies.${index}`,
              "Army permanent printed stats must match the embedded catalog definition.",
            ),
          );
        }
      }
    }
  }

  if (state.schemaVersion === 5 && state.status.type === "finished") return violations;

  let snapshot;
  try {
    snapshot = getSupplySnapshot(state);
  } catch (error) {
    violations.push(
      violation(
        "SUPPLY_DEFINITION_RESOLUTION_FAILED",
        "catalog",
        error instanceof Error ? error.message : "Supply definition resolution failed.",
      ),
    );
    return violations;
  }

  if (state.supplyStatus.type === "not-started") {
    if (state.phase === "supply") {
      violations.push(
        violation(
          "SUPPLY_NOT_RESOLVED_ON_ENTRY",
          "supplyStatus",
          "A schema-V3 state in Supply must be awaiting discard or resolved.",
        ),
      );
    }
    return violations;
  }

  const validSupplyPhase =
    state.supplyStatus.type === "awaiting-upkeep-discard"
      ? state.phase === "supply"
      : state.phase === "supply" || (state.schemaVersion === 5 && state.phase === "action-window");
  if (
    !validSupplyPhase ||
    state.supplyStatus.playerId !== state.activePlayerId ||
    state.supplyStatus.turnNumber !== state.turnNumber
  ) {
    violations.push(
      violation(
        "SUPPLY_STATUS_CONTEXT_MISMATCH",
        "supplyStatus",
        "Supply status must belong to the active player and current Supply Phase turn.",
      ),
    );
  }

  if (state.supplyStatus.type === "awaiting-upkeep-discard") {
    if (snapshot.unsustainableLaneIds.length === 0) {
      violations.push(
        violation(
          "SUPPLY_AWAITING_WITHOUT_SHORTFALL",
          "supplyStatus",
          "Awaiting Supply must contain at least one unsustainable lane.",
        ),
      );
    }
    if (!isZeroResourcePool(state.players[state.activePlayerId].kingdomPool)) {
      violations.push(
        violation(
          "SUPPLY_TRANSFERRED_BEFORE_RESOLUTION",
          `players.${state.activePlayerId}.kingdomPool`,
          "No resources may enter the Kingdom Pool before all local Upkeep resolves.",
        ),
      );
    }
  } else if (state.schemaVersion !== 5) {
    if (snapshot.unsustainableLaneIds.length > 0) {
      violations.push(
        violation(
          "SUPPLY_RESOLVED_WITH_SHORTFALL",
          "supplyStatus",
          "Resolved Supply cannot contain an unsustainable lane.",
        ),
      );
    }
    if (
      !resourcesEqual(
        state.players[state.activePlayerId].kingdomPool,
        snapshot.kingdomPoolContribution,
      )
    ) {
      violations.push(
        violation(
          "SUPPLY_KINGDOM_POOL_MISMATCH",
          `players.${state.activePlayerId}.kingdomPool`,
          "Resolved Kingdom Pool must equal the exact sum of local remainders.",
        ),
      );
    }
  }

  return violations;
}

function isEmptyTurnActionUsage(usage: Readonly<TurnActionUsage>): boolean {
  return (
    usage.landActions === 0 &&
    usage.deploymentActions === 0 &&
    usage.governanceActions === 0 &&
    usage.eventsPlayed === 0 &&
    usage.eventAuthorizationLanesUsed.length === 0 &&
    usage.attacksMadeFromLanes.length === 0
  );
}

/**
 * Ready is the observable boundary immediately after an authoritative Cleanup
 * handoff. Detect stale incoming data instead of silently repairing the
 * opponent's completed-turn state during the next player's transition.
 */
function inspectReadyTurnBoundary(state: Readonly<GameState>): readonly StateInvariantViolation[] {
  if (state.phase !== "ready" || state.status.type !== "active") return [];

  const violations: StateInvariantViolation[] = [];
  // Turn one begins after initial setup, so the non-active player may still
  // hold its opening hand. Every later Ready state follows a Cleanup handoff.
  if (state.turnNumber > 1) {
    const readyPlayerId = state.activePlayerId;
    const readyPlayer = state.players[readyPlayerId];
    if (!isEmptyTurnActionUsage(readyPlayer.actionsUsed)) {
      violations.push(
        violation(
          "STALE_READY_PLAYER_ACTION_USAGE",
          `players.${readyPlayerId}.actionsUsed`,
          "The active player must enter Ready with empty per-turn action usage.",
        ),
      );
    }
    if (!isZeroResourcePool(readyPlayer.kingdomPool)) {
      violations.push(
        violation(
          "STALE_READY_PLAYER_KINGDOM_POOL",
          `players.${readyPlayerId}.kingdomPool`,
          "The active player must enter Ready with an empty Kingdom Pool.",
        ),
      );
    }
    if (
      (state.schemaVersion === 3 || state.schemaVersion === 4 || state.schemaVersion === 5) &&
      state.supplyStatus.type !== "not-started"
    ) {
      violations.push(
        violation(
          "STALE_READY_SUPPLY_STATUS",
          "supplyStatus",
          "The active player must enter Ready with a not-started Supply lifecycle.",
        ),
      );
    }

    const previousPlayerId = getOpponentPlayerId(readyPlayerId);
    const previousPlayer = state.players[previousPlayerId];
    if (previousPlayer.hand.length !== 0) {
      violations.push(
        violation(
          "CLEANUP_HAND_NOT_EMPTY",
          `players.${previousPlayerId}.hand`,
          "The outgoing player's hand must be empty after Cleanup.",
        ),
      );
    }
    if (!isEmptyTurnActionUsage(previousPlayer.actionsUsed)) {
      violations.push(
        violation(
          "CLEANUP_ACTION_USAGE_NOT_EMPTY",
          `players.${previousPlayerId}.actionsUsed`,
          "The outgoing player's per-turn action usage must be empty after Cleanup.",
        ),
      );
    }
    if (!isZeroResourcePool(previousPlayer.kingdomPool)) {
      violations.push(
        violation(
          "CLEANUP_KINGDOM_POOL_NOT_EMPTY",
          `players.${previousPlayerId}.kingdomPool`,
          "The outgoing player's Kingdom Pool must be empty after Cleanup.",
        ),
      );
    }
  }

  return violations;
}

function inspectFlankKingdomCompleteState(
  state: Readonly<GameState>,
): readonly StateInvariantViolation[] {
  if (state.schemaVersion !== 4 && state.schemaVersion !== 5) return [];
  const violations: StateInvariantViolation[] = [];
  for (const playerId of PLAYER_IDS) {
    const progress = state.kingdomProgress[playerId];
    const expected = LANE_IDS.filter((laneId) => progress.populatedLaneIds.includes(laneId));
    if (
      progress.populatedLaneIds.length !== expected.length ||
      !progress.populatedLaneIds.every((laneId, index) => laneId === expected[index]) ||
      progress.complete !== (expected.length === LANE_IDS.length)
    ) {
      violations.push(
        violation(
          "INVALID_KINGDOM_PROGRESS",
          `kingdomProgress.${playerId}`,
          "Kingdom progress must have unique canonical lanes and completion must match all three.",
        ),
      );
    }
    const attacks = state.players[playerId].actionsUsed.attacksMadeFromLanes;
    if (new Set(attacks).size !== attacks.length) {
      violations.push(
        violation(
          "DUPLICATE_SOURCE_ATTACK",
          `players.${playerId}.actionsUsed.attacksMadeFromLanes`,
          "A source lane may appear at most once in one Battle Phase.",
        ),
      );
    }
  }
  const warning = state.noTerritoryWarning;
  if (warning !== null) {
    if (
      state.status.type !== "active" ||
      warning.threatenedPlayerId === warning.threateningPlayerId
    ) {
      violations.push(
        violation(
          "INVALID_NO_TERRITORY_WARNING",
          "noTerritoryWarning",
          "A warning must reference distinct players while the match remains active.",
        ),
      );
    }
    if (
      warning.deadlineTurnNumber !== null &&
      warning.deadlineTurnNumber < warning.createdTurnNumber
    ) {
      violations.push(
        violation(
          "INVALID_NO_TERRITORY_DEADLINE",
          "noTerritoryWarning.deadlineTurnNumber",
          "A No Territory deadline cannot precede warning creation.",
        ),
      );
    }
    if (
      getActiveLandLaneIds(state, warning.threatenedPlayerId).length > 0 ||
      getActiveLandLaneIds(state, warning.threateningPlayerId).length === 0
    ) {
      violations.push(
        violation(
          "STALE_NO_TERRITORY_WARNING",
          "noTerritoryWarning",
          "A warning must clear when territory is restored or the threat loses all Active Lands.",
        ),
      );
    }
  }
  return violations;
}

export function getStateInvariantViolations(
  state: Readonly<GameState>,
): readonly StateInvariantViolation[] {
  const violations: StateInvariantViolation[] = [];

  if (state.turnNumber < 1 || !Number.isSafeInteger(state.turnNumber)) {
    violations.push(
      violation(
        "INVALID_TURN_NUMBER",
        "turnNumber",
        "Turn number must be a positive safe integer.",
      ),
    );
  }

  if (
    state.random.algorithm !== "xorshift32" ||
    state.random.seed !== state.seed ||
    !Number.isSafeInteger(state.random.value) ||
    state.random.value < 0 ||
    state.random.value > 0xffff_ffff ||
    !Number.isSafeInteger(state.random.draws) ||
    state.random.draws < 0
  ) {
    violations.push(
      violation(
        "INVALID_RANDOM_STATE",
        "random",
        "Seeded random state must be replayable and use finite uint32 bookkeeping.",
      ),
    );
  }

  if (state.status.type === "finished" && state.phase !== "finished") {
    violations.push(
      violation(
        "FINISHED_MATCH_WRONG_PHASE",
        "phase",
        "A finished match must use the finished phase.",
      ),
    );
  }

  if (state.status.type === "active" && state.phase === "finished") {
    violations.push(
      violation(
        "ACTIVE_MATCH_FINISHED_PHASE",
        "phase",
        "An active match cannot use the finished phase.",
      ),
    );
  }

  const claimedLanes: Record<PlayerId, number> = {
    "player-1": 0,
    "player-2": 0,
  };

  for (const laneId of LANE_IDS) {
    const lane = state.lanes[laneId];
    if (lane.laneId !== laneId) {
      violations.push(
        violation(
          "LANE_ID_MISMATCH",
          `lanes.${laneId}.laneId`,
          "The lane record key and laneId must match.",
        ),
      );
    }

    violations.push(
      ...inspectLaneSide(state, laneId, "player-1", lane.players["player-1"]),
      ...inspectLaneSide(state, laneId, "player-2", lane.players["player-2"]),
    );

    if (lane.result === "active") {
      for (const playerId of PLAYER_IDS) {
        const status = lane.players[playerId].status;
        if (status === "committed" || status === "defeated") {
          violations.push(
            violation(
              "ACTIVE_LANE_HAS_RESOLVED_SIDE",
              `lanes.${laneId}.players.${playerId}.status`,
              "An active lane cannot contain a committed or defeated side.",
            ),
          );
        }
      }
    } else {
      const winner = lane.result === "player-1-won" ? "player-1" : "player-2";
      const loser = winner === "player-1" ? "player-2" : "player-1";
      claimedLanes[winner] += 1;

      if (
        lane.players[winner].status !== "committed" ||
        lane.players[loser].status !== "defeated"
      ) {
        violations.push(
          violation(
            "RESOLVED_LANE_STATUS_MISMATCH",
            `lanes.${laneId}`,
            "A resolved lane must commit the winner and defeat the loser.",
          ),
        );
      }
    }
  }

  for (const playerId of PLAYER_IDS) {
    const banners = state.players[playerId].victoryBanners;
    if (state.schemaVersion === 5 && state.players[playerId].hand.length > MAX_HAND_SIZE) {
      violations.push(
        violation(
          "HAND_LIMIT_EXCEEDED",
          `players.${playerId}.hand`,
          `A public-beta hand cannot contain more than ${String(MAX_HAND_SIZE)} cards.`,
        ),
      );
    }
    if (banners !== claimedLanes[playerId]) {
      violations.push(
        violation(
          "VICTORY_BANNER_MISMATCH",
          `players.${playerId}.victoryBanners`,
          "Victory Banners must equal the number of lanes claimed.",
        ),
      );
    }

    if (state.status.type === "active" && banners >= 2) {
      violations.push(
        violation(
          "UNRESOLVED_MATCH_VICTORY",
          `players.${playerId}.victoryBanners`,
          "A player with two Victory Banners must win immediately.",
        ),
      );
    }
  }

  violations.push(...inspectCardInstanceUniqueness(state));
  violations.push(...inspectSetupState(state));
  violations.push(...inspectSupplyState(state));
  violations.push(...inspectReadyTurnBoundary(state));
  violations.push(...inspectFlankKingdomCompleteState(state));

  if (
    state.status.type === "finished" &&
    state.status.reason === "two-victory-banners" &&
    state.players[state.status.winnerPlayerId].victoryBanners < 2
  ) {
    violations.push(
      violation(
        "WINNER_LACKS_TWO_BANNERS",
        `players.${state.status.winnerPlayerId}.victoryBanners`,
        "The match winner must have at least two Victory Banners.",
      ),
    );
  }

  return violations;
}
